import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Mic, MicOff, Stop } from '@mui/icons-material';

/**
 * VoiceInput - Microphone button for speech-to-text transcription
 * Inserts text at cursor position and maintains cursor placement
 */
const VoiceInput = ({ onTranscript, currentValue = '', inputRef = null, disabled = false }) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef(null);
  const cursorPositionRef = useRef(null);
  const pendingCursorPositionRef = useRef(null);

  useEffect(() => {
    // Check if Web Speech API is supported
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setIsSupported(false);
      return;
    }

    // Initialize Speech Recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + ' ';
        }
      }

      if (finalTranscript) {
        const transcribedText = finalTranscript.trim();
        const cursorPos = cursorPositionRef.current !== null ? cursorPositionRef.current : currentValue.length;
        
        // Insert at cursor position
        const before = currentValue.substring(0, cursorPos);
        const after = currentValue.substring(cursorPos);
        const spaceBefore = before && !before.endsWith(' ') ? ' ' : '';
        const spaceAfter = after && !after.startsWith(' ') ? ' ' : '';
        const newText = before + spaceBefore + transcribedText + spaceAfter + after;
        
        // Calculate new cursor position (after inserted text)
        const newCursorPos = cursorPos + spaceBefore.length + transcribedText.length + spaceAfter.length;
        pendingCursorPositionRef.current = newCursorPos;
        
        // Update the text
        onTranscript(newText);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please enable microphone permissions in your browser.');
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [currentValue, onTranscript]);

  // Restore cursor position after text update
  useEffect(() => {
    if (pendingCursorPositionRef.current !== null && inputRef?.current) {
      const targetPosition = pendingCursorPositionRef.current;
      
      // Find the actual input element
      let actualInput = inputRef.current;
      if (actualInput.querySelector) {
        actualInput = actualInput.querySelector('textarea') || actualInput.querySelector('input');
      }
      
      if (actualInput && actualInput.setSelectionRange) {
        // Use setTimeout to ensure React has finished rendering
        setTimeout(() => {
          actualInput.focus();
          actualInput.setSelectionRange(targetPosition, targetPosition);
          pendingCursorPositionRef.current = null;
        }, 0);
      }
    }
  }, [currentValue, inputRef]);

  const getCursorPosition = () => {
    if (!inputRef?.current) return currentValue.length;
    
    let actualInput = inputRef.current;
    
    // For MUI TextField, find the actual input/textarea
    if (actualInput.querySelector) {
      actualInput = actualInput.querySelector('textarea') || actualInput.querySelector('input');
    }
    
    if (actualInput && typeof actualInput.selectionStart === 'number') {
      return actualInput.selectionStart;
    }
    
    return currentValue.length;
  };

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      // Capture cursor position
      cursorPositionRef.current = getCursorPosition();
      
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error('Failed to start recognition:', error);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  if (!isSupported) {
    return (
      <Tooltip title="Speech-to-text not supported in this browser. Try Chrome or Edge.">
        <IconButton disabled size="small">
          <MicOff sx={{ fontSize: 20, color: '#999' }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={isListening ? "Click to stop recording" : "Click to start voice input"}>
      <IconButton
        onClick={isListening ? stopListening : startListening}
        disabled={disabled}
        size="small"
        sx={{
          color: isListening ? '#e74c3c' : '#6B3FA0',
          '&:hover': {
            backgroundColor: isListening ? 'rgba(231, 76, 60, 0.1)' : 'rgba(107, 63, 160, 0.1)'
          }
        }}
      >
        {isListening ? (
          <Stop sx={{ fontSize: 20 }} />
        ) : (
          <Mic sx={{ fontSize: 20 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default VoiceInput;
